import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  User,
  Mail,
  Building,
  Crown,
  Shield,
  UserCheck,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';
import type { EmpresaListItem, UsuarioAuthService, AsignarUsuarioRequest } from '../../../types';
import { useBuscarUsuarios, useAsignarUsuario, getPermisosDefecto } from '../../../hooks/useUsuariosEmpresa';

interface UserSearchModalProps {
  empresa: EmpresaListItem;
  isOpen: boolean;
  onClose: () => void;
  onUserAssigned: () => void;
}

const UserSearchModal: React.FC<UserSearchModalProps> = ({
  empresa,
  isOpen,
  onClose,
  onUserAssigned
}) => {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState({
    tiene_acceso_mava: true,
    sin_empresa: false,
    page: 1,
    per_page: 10
  });
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<UsuarioAuthService | null>(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [asignacionData, setAsignacionData] = useState({
    rol_en_empresa: 'operador' as 'administrador' | 'supervisor' | 'operador',
    es_empresa_principal: false,
    permisos: getPermisosDefecto('operador')
  });

  // Hooks
  const buscarUsuarios = useBuscarUsuarios({ 
    busqueda: busqueda.trim() || undefined, 
    ...filtros 
  });
  const asignarUsuario = useAsignarUsuario();

  useEffect(() => {
    if (busqueda.trim()) {
      buscarUsuarios.refetch();
    }
  }, [busqueda, filtros]);

  useEffect(() => {
    // Actualizar permisos cuando cambia el rol
    setAsignacionData(prev => ({
      ...prev,
      permisos: getPermisosDefecto(prev.rol_en_empresa)
    }));
  }, [asignacionData.rol_en_empresa]);

  if (!isOpen) {
    return null;
  }

  const handleSearch = () => {
    setFiltros(prev => ({ ...prev, page: 1 }));
    buscarUsuarios.refetch();
  };

  const handleSelectUser = (usuario: UsuarioAuthService) => {
    setUsuarioSeleccionado(usuario);
    setShowAssignForm(true);
  };

  const handleAssignUser = async () => {
    if (!usuarioSeleccionado) return;

    try {
      const request: AsignarUsuarioRequest = {
        usuario_id: usuarioSeleccionado.id,
        rol_en_empresa: asignacionData.rol_en_empresa,
        es_empresa_principal: asignacionData.es_empresa_principal,
        permisos: asignacionData.permisos
      };

      await asignarUsuario.mutateAsync({
        empresaId: empresa.id,
        datos: request
      });

      // Reset y cerrar
      setUsuarioSeleccionado(null);
      setShowAssignForm(false);
      onUserAssigned();
    } catch (error) {
      console.error('Error al asignar usuario:', error);
    }
  };

  const handleClose = () => {
    setBusqueda('');
    setUsuarioSeleccionado(null);
    setShowAssignForm(false);
    buscarUsuarios.remove();
    onClose();
  };

  const handlePermisoChange = (permiso: string, valor: boolean) => {
    setAsignacionData(prev => ({
      ...prev,
      permisos: {
        ...prev.permisos,
        [permiso]: valor
      }
    }));
  };

  const usuarios = buscarUsuarios.data?.usuarios || [];
  const isLoading = buscarUsuarios.isLoading || buscarUsuarios.isFetching;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <UserCheck className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('admin.companies')} - {t('admin.add')}
              </h2>
              <p className="text-sm text-gray-600">
                {empresa.nombre} • {empresa.rut}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {!showAssignForm ? (
          // PASO 1: Búsqueda y selección de usuario
          <>
            {/* Búsqueda */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex space-x-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder={t('admin.search_placeholder')}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <button
                  onClick={handleSearch}
                  disabled={!busqueda.trim() || isLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : t('common.search')}
                </button>
              </div>

              {/* Filtros */}
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filtros.tiene_acceso_mava}
                    onChange={(e) => setFiltros(prev => ({ ...prev, tiene_acceso_mava: e.target.checked }))}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">{t('admin.status_active')}</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filtros.sin_empresa}
                    onChange={(e) => setFiltros(prev => ({ ...prev, sin_empresa: e.target.checked }))}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">{t('admin.companies')}</span>
                </label>
              </div>
            </div>

            {/* Resultados */}
            <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '400px' }}>
              {buscarUsuarios.error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-red-700">
                      {t('errors.load_error')}: {buscarUsuarios.error.message}
                    </p>
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="text-center py-8">
                  <Loader className="h-8 w-8 animate-spin text-green-600 mx-auto mb-2" />
                  <p className="text-gray-600">{t('common.loading')}</p>
                </div>
              )}

              {!isLoading && busqueda.trim() && usuarios.length === 0 && (
                <div className="text-center py-8">
                  <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('common.no_results')}
                  </h3>
                  <p className="text-gray-600">
                    {t('common.clear_filters')}
                  </p>
                </div>
              )}

              {!isLoading && !busqueda.trim() && (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('common.search')}
                  </h3>
                  <p className="text-gray-600">
                    {t('admin.search_placeholder')}
                  </p>
                </div>
              )}

              {/* Lista de usuarios */}
              {usuarios.length > 0 && (
                <div className="space-y-3">
                  {usuarios.map((usuario) => (
                    <div
                      key={usuario.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleSelectUser(usuario)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-green-600" />
                          </div>
                          
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-gray-900">
                                {usuario.first_name} {usuario.last_name}
                              </p>
                              {!usuario.is_active && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  {t('admin.status_inactive')}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <User className="h-3 w-3" />
                                <span>@{usuario.username}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Mail className="h-3 w-3" />
                                <span>{usuario.email}</span>
                              </div>
                              {usuario.empresas_asignadas_count > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Building className="h-3 w-3" />
                                  <span>{usuario.empresas_asignadas_count} empresa(s)</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {usuario.tiene_acceso_mava ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-orange-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          // PASO 2: Formulario de asignación
          <>
            {/* Usuario seleccionado */}
            <div className="p-6 bg-green-50 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {usuarioSeleccionado?.first_name} {usuarioSeleccionado?.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    @{usuarioSeleccionado?.username} • {usuarioSeleccionado?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Formulario de asignación */}
            <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: '500px' }}>
              {/* Rol */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.status')}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'operador', label: 'Operador', icon: User, color: 'gray' },
                    { value: 'supervisor', label: 'Supervisor', icon: Shield, color: 'blue' },
                    { value: 'administrador', label: 'Administrador', icon: Crown, color: 'yellow' }
                  ].map((rol) => {
                    const Icon = rol.icon;
                    const isSelected = asignacionData.rol_en_empresa === rol.value;
                    
                    return (
                      <button
                        key={rol.value}
                        type="button"
                        onClick={() => setAsignacionData(prev => ({ ...prev, rol_en_empresa: rol.value as any }))}
                        className={`p-3 border-2 rounded-lg transition-colors ${
                          isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <Icon className={`h-6 w-6 ${
                            isSelected ? 'text-green-600' : 'text-gray-400'
                          }`} />
                          <span className={`text-sm font-medium ${
                            isSelected ? 'text-green-700' : 'text-gray-600'
                          }`}>
                            {rol.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Configuraciones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Configuraciones
                </label>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={asignacionData.es_empresa_principal}
                      onChange={(e) => setAsignacionData(prev => ({ ...prev, es_empresa_principal: e.target.checked }))}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Empresa principal</span>
                      <p className="text-xs text-gray-600">Esta será la empresa principal del usuario</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Permisos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Permisos específicos
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { key: 'puede_crear_reportes', label: 'Crear reportes', desc: 'Puede crear nuevos reportes' },
                    { key: 'puede_editar_reportes_propios', label: 'Editar reportes propios', desc: 'Puede editar sus propios reportes' },
                    { key: 'puede_ver_reportes_otros', label: 'Ver reportes de otros', desc: 'Puede ver reportes de otros usuarios' },
                    { key: 'puede_editar_reportes_otros', label: 'Editar reportes de otros', desc: 'Puede editar reportes de otros usuarios' },
                    { key: 'puede_aprobar_reportes', label: 'Aprobar reportes', desc: 'Puede aprobar reportes pendientes' },
                    { key: 'puede_gestionar_referencias', label: 'Gestionar referencias', desc: 'Puede gestionar faenas, predios, etc.' },
                    { key: 'puede_gestionar_usuarios', label: 'Gestionar usuarios', desc: 'Puede gestionar usuarios de la empresa' }
                  ].map((permiso) => (
                    <label key={permiso.key} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={asignacionData.permisos[permiso.key as keyof typeof asignacionData.permisos]}
                        onChange={(e) => handlePermisoChange(permiso.key, e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{permiso.label}</span>
                        <p className="text-xs text-gray-600">{permiso.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer de asignación */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowAssignForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={handleAssignUser}
                  disabled={asignarUsuario.isLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {asignarUsuario.isLoading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>{t('common.saving')}</span>
                    </>
                  ) : (
                    <>
                      <UserCheck className="h-4 w-4" />
                      <span>{t('admin.add')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserSearchModal;
